import React, { useEffect, useState } from 'react';
import { AuthProvider, useAuth } from '../../context/AuthContext';
import { capitalize } from '../../utils/string';
import {
    BrowserRouter,
    Routes,
    Route,
    useNavigate,
    useLocation,
} from 'react-router';
import Api from '../../services/api';
import { groupNameBySlug } from '../../utils/mappings';
import { UserOutlined } from '@ant-design/icons';
import {
    Layout as AntLayout,
    Menu,
    Image,
    Divider,
    Flex,
    Avatar,
    Space,
    Button,
    theme,
} from 'antd';
import PrivateRoute from '../PrivateRoute';
import AuthCallback from '../../pages/auth/AuthCallback';
import Reports from '../../pages/reports/Reports';
import Report from '../../pages/reports/Report';
import NotFound from '../../pages/errors/NotFound';
import Templates from '../../pages/templates/Templates';
import Submission from '../../pages/submission/Submission';
import Decisions from '../../pages/decisions/Decisions';

const { Header, Content, Sider } = AntLayout;

const AuthAntLayout = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { token: { colorBgContainer } } = theme.useToken();
    const {
        user,
        userGroups,
        setUserGroups,
        authUserBelongsToGroups,
        logout
    } = useAuth();
    const resourceName = location.pathname.split('/')[1];

    /**
     * Sincroniza os grupos do usuário autenticado.
     */
    async function syncUserGroups() {
        const { data } = await Api('users/my_groups', { noPrefix: true }).index()

        setUserGroups(data);
    }

    useEffect(() => {
        // sincroniza os grupos do usuário autenticado
        // a cada requisição.
        syncUserGroups();
    }, []);

    /**
     * Condiciona a criação do menu lateral.
     * 
     * @param {Array} groups
     * @returns {Array}
     */
    const mountGroupBasedMenu = groups => {
        return Object.keys(groups).map(groupName => {
            const group = groups[groupName]

            if (!group?.canView) {
                return false;
            }

            return {
                key: groupName.toLowerCase(),
                label: groupName,
                type: 'group',
                children: [
                    ...group.children.filter(children => children?.canView),
                    { type: 'divider' }
                ],
            }
        })
    }

    // se o usuário autenticado pertence a um grupo com privilégio:
    const authUserBelongsToMainGroup = authUserBelongsToGroups([
        'superadministrador',
        'administrador',
        'lotacao:secretaria_da_corregedoria_geral'
    ])

    const menuItems = mountGroupBasedMenu({
        'Principais': {
            canView: userGroups.length > 0,
            children: [
                {
                    canView: true,
                    label: 'Relatórios Mensais de Atividades',
                    key: 'reports',
                },
                {
                    canView: authUserBelongsToMainGroup,
                    label: 'Decisões',
                    key: 'decisions'
                },
                // ...
            ]
        },
        'Metabase': {
            canView: authUserBelongsToMainGroup,
            children: [
                {
                    canView: authUserBelongsToMainGroup,
                    label: (
                        <a
                            style={{ color: 'green' }}
                            href={`${process.env.REACT_APP_METABASE_URL}/dashboard/134-monitoramento`}
                            target="_blank"
                        >
                            Estatísticas Sistema de RMA
                        </a>
                    ),
                    key: 'href--metabase-monitoramento'
                },
                {
                    canView: authUserBelongsToMainGroup,
                    label: (
                        <a href={`${process.env.REACT_APP_METABASE_URL}/dashboard/5-rma-corregedoria?tab=167`} target="_blank">
                            RMA - Corregedoria
                        </a>
                    ),
                    key: 'href--metabase-corregedoria'
                }
            ]
        },
        'Configurações': {
            canView: authUserBelongsToGroups(['superadministrador', 'administrador']),
            children: [
                { canView: true, label: 'Gerenciar Modelos de RMAs', key: 'templates' },
                {
                    canView: authUserBelongsToGroups(['superadministrador']),
                    label: (
                        <a href={`${process.env.REACT_APP_API_URL}/admin`} target="_blank">
                            Django Admin
                        </a>
                    ),
                    key: 'href--django-admin'
                },
            ]
        }
    })

    return (
        <AntLayout style={{ minHeight: '100vh' }}>
            {userGroups.length > 0 && (
                <Sider style={{ background: colorBgContainer }} width={300}>
                    <div style={{ width: '100%', textAlign: 'center', paddingTop: 30 }}>
                        <Image
                            src="https://defensoria.mt.def.br/dpmt/conteudo/xfthumbs/logo-branca.jpg"
                            width={100}
                            preview={false}
                        />
                    </div>

                    <Divider />

                    <Menu
                        onClick={({ key }) => {
                            // condiciona ao não-roteamento, quando se
                            // tratar de um link externo:
                            if (key.includes('href--')) {
                                return;
                            }

                            navigate(`/${key}`)
                        }}
                        defaultSelectedKeys={[resourceName]}
                        mode="inline"
                        items={menuItems}
                    />
                </Sider>
            )}

            <AntLayout>
                <Header style={{ background: colorBgContainer }}>
                    <Flex justify="end" align="center">
                        <Space size="middle">
                            <div style={{ lineHeight: 1.2, textAlign: 'right' }}>
                                <p>Olá, <b>{capitalize(user.first_name)}</b></p>
                                <p>
                                    <small>{userGroups.filter(group => group.name.includes('lotacao:')).map(group => groupNameBySlug[group.name]).join(", ")}</small>
                                </p>
                            </div>

                            <Avatar icon={<UserOutlined />} />

                            <Button type="link" onClick={logout}>Sair</Button>
                        </Space>
                    </Flex>
                </Header>

                <Content style={{ margin: '2.5rem' }}>
                    <Routes>
                        <Route path="/" element={<Reports />} />
                        {/* Relatórios */}
                        <Route path="/reports" element={<Reports />} />
                        <Route path="/reports/:id" element={<Report />} />
                        {/* Decisões */}
                        <Route path="/decisions" element={<Decisions />} />
                        {/* Templates */}
                        <Route path="/templates" element={<Templates />} />
                        {/* Rotas desconhecidas: */}
                        <Route path="*" element={<NotFound />}/>
                    </Routes>
                </Content>
            </AntLayout>
        </AntLayout>
    )
}

const Layout = () => {
    useEffect(() => {
        document.title = 'DPEMT - Relatórios Mensais de Atividades';
    }, []);

    return (
        <BrowserRouter>
            <AuthProvider>
                <Routes>
                    {/* Rotas públicas: */}
                    <Route path="/auth/callback" element={<AuthCallback />} />
                    <Route path="/relatorios/:uuid/manifestacao" element={<Submission />} />

                    <Route
                        path="/*"
                        element={
                            <PrivateRoute>
                                <AuthAntLayout />
                            </PrivateRoute>
                        }
                    />
                </Routes>
            </AuthProvider>
        </BrowserRouter>
    );
}

export default Layout;
